import type { Ecriture } from '@shared/types/Ecriture';
import type { Immobilisation } from '@shared/types/Immobilisation';
import { AmortissementsService } from './AmortissementsService';

export interface NoteAnnexe {
  numero: string;
  titre: string;
  contenu: Record<string, number | string>;
}

/**
 * Génération des 10 notes annexes SYSCOHADA Révisé obligatoires.
 * AUDCIF Art. 33.
 */
export class NotesAnnexesService {
  static genererToutes(
    ecritures: readonly Ecriture[],
    immobilisations: readonly Immobilisation[],
    exercice: string,
  ): NoteAnnexe[] {
    return [
      this.note1_immobilisations(immobilisations, exercice),
      this.note2_amortissements(immobilisations, exercice),
      this.note3_provisions(ecritures),
      this.note4_creances(ecritures),
      this.note5_stocks(ecritures),
      this.note6_capitaux(ecritures),
      this.note7_emprunts(ecritures),
      this.note8_chiffre_affaires(ecritures, exercice),
      this.note9_charges_personnel(ecritures),
      this.note10_engagements(ecritures),
    ];
  }

  private static note1_immobilisations(
    immos: readonly Immobilisation[],
    exercice: string,
  ): NoteAnnexe {
    const annee = exercice.slice(0, 4);
    const total = immos.reduce((s, i) => s + i.valeur_acquisition, 0);
    const acquisitions = immos
      .filter((i) => (i.date_acquisition || '').startsWith(annee))
      .reduce((s, i) => s + i.valeur_acquisition, 0);
    const cessions = immos
      .filter((i) => i.cede && (i.date_cession || '').startsWith(annee))
      .reduce((s, i) => s + i.valeur_acquisition, 0);
    return {
      numero: '1',
      titre: 'Tableau des immobilisations brutes',
      contenu: {
        nombre: immos.length,
        total_acquisition: total,
        acquisitions_exercice: acquisitions,
        cessions_exercice: cessions,
      },
    };
  }
  private static note2_amortissements(
    immos: readonly Immobilisation[],
    exercice: string,
  ): NoteAnnexe {
    const annee = Number(exercice.slice(0, 4)) || new Date().getFullYear();
    let dotation_exercice = 0;
    let cumul_amortissements = 0;
    let vnc = 0;
    for (const immo of immos) {
      const debut = Number((immo.date_mise_service || immo.date_acquisition).slice(0, 4));
      const rang = debut ? annee - debut + 1 : 1;
      const plan = AmortissementsService.planAmortissement(immo);
      const ligne = rang > 0 ? plan[rang - 1] : undefined;
      dotation_exercice += AmortissementsService.dotationPourAnneeCivile(immo, annee);
      if (ligne) {
        cumul_amortissements += ligne.cumul_amortissements;
        vnc += ligne.vnc;
      } else if (rang <= 0) {
        vnc += immo.valeur_acquisition;
      } else {
        const last = plan[plan.length - 1];
        cumul_amortissements += last?.cumul_amortissements ?? 0;
        vnc += last?.vnc ?? 0;
      }
    }
    return {
      numero: '2',
      titre: 'Tableau des amortissements',
      contenu: {
        nombre: immos.length,
        exercice: annee,
        dotation_exercice,
        cumul_amortissements,
        vnc,
      },
    };
  }
  private static note3_provisions(ecritures: readonly Ecriture[]): NoteAnnexe {
    const provisions = ecritures.filter(
      (e) => e.compte.startsWith('19') || e.compte.startsWith('491') || e.compte.startsWith('198'),
    );
    const total = provisions.reduce((s, e) => s + (e.credit - e.debit), 0);
    return {
      numero: '3',
      titre: 'État des provisions',
      contenu: { lignes: provisions.length, montant_net: total },
    };
  }
  private static note4_creances(ecritures: readonly Ecriture[]): NoteAnnexe {
    const c41 = ecritures.filter((e) => e.compte.startsWith('41'));
    const total = c41.reduce((s, e) => s + (e.debit - e.credit), 0);
    return {
      numero: '4',
      titre: 'État des créances clients',
      contenu: { lignes: c41.length, encours: total },
    };
  }
  private static note5_stocks(ecritures: readonly Ecriture[]): NoteAnnexe {
    const s3 = ecritures.filter((e) => e.compte.startsWith('3'));
    const total = s3.reduce((s, e) => s + (e.debit - e.credit), 0);
    return {
      numero: '5',
      titre: 'Variation des stocks',
      contenu: { lignes: s3.length, solde: total },
    };
  }
  private static note6_capitaux(ecritures: readonly Ecriture[]): NoteAnnexe {
    const c10 = ecritures.filter((e) => e.compte.startsWith('10') || e.compte.startsWith('11'));
    return {
      numero: '6',
      titre: 'Tableau de variation des capitaux propres',
      contenu: { lignes: c10.length },
    };
  }
  private static note7_emprunts(ecritures: readonly Ecriture[]): NoteAnnexe {
    const c16 = ecritures.filter((e) => e.compte.startsWith('16'));
    const total = c16.reduce((s, e) => s + (e.credit - e.debit), 0);
    return {
      numero: '7',
      titre: 'Emprunts et dettes financières',
      contenu: { lignes: c16.length, encours_du: total },
    };
  }
  private static note8_chiffre_affaires(
    ecritures: readonly Ecriture[],
    exercice: string,
  ): NoteAnnexe {
    const ca = ecritures
      .filter((e) => e.compte.startsWith('70') && e.periode.startsWith(exercice))
      .reduce((s, e) => s + (e.credit - e.debit), 0);
    return { numero: '8', titre: 'Chiffre d affaires', contenu: { exercice, ca } };
  }
  private static note9_charges_personnel(ecritures: readonly Ecriture[]): NoteAnnexe {
    const c66 = ecritures.filter((e) => e.compte.startsWith('66'));
    const total = c66.reduce((s, e) => s + (e.debit - e.credit), 0);
    return { numero: '9', titre: 'Charges de personnel', contenu: { lignes: c66.length, total } };
  }
  private static note10_engagements(_ecritures: readonly Ecriture[]): NoteAnnexe {
    return {
      numero: '10',
      titre: 'Engagements hors bilan',
      contenu: { commentaire: 'À compléter manuellement' },
    };
  }
}
